/**
 * Sign-in (prompt.md §6.1, §52).
 *
 * The full authorization-code + PKCE flow, split across two legs:
 *
 *   1. `startSignIn` makes a PKCE pair, keeps the verifier in `sessionStorage`,
 *      and sends the browser to the provider via our `/start` endpoint.
 *   2. The provider redirects to *our* API, which mints a single-use code and
 *      bounces the browser back here with `?code=`. `completeSignIn` redeems that
 *      code for a session token.
 *
 * The session token is therefore never in a URL. An earlier version of this page
 * read the token out of the redirect fragment, which puts it in browser history
 * and hands it to anything that can observe the URL.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { DayviseBrand, Button, Card, stackStyle, MUTED_TEXT } from "@dayvise/react-ui";
import { useSession, useTokenStore } from "@dayvise/react-app-core";
import type { SessionWorkspace } from "@dayvise/react-app-core";

import { endpoints, resolveApiBaseUrl } from "../config.ts";
import { clearPendingSignIn, requireSignInVerifier, savePendingSignIn } from "../auth-flow.ts";

const SESSION_STORAGE_KEY = "dayvise.session";

/**
 * The browser token store. `sessionStorage` rather than `localStorage`: closing
 * the tab ends the session, which is the right default for a shared machine.
 */
export const browserTokenStore = {
  async read(): Promise<string | null> {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  },
  async write(token: string): Promise<void> {
    sessionStorage.setItem(SESSION_STORAGE_KEY, token);
  },
  async clear(): Promise<void> {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  },
};

/* --------------------------------- PKCE ---------------------------------- */

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Mirrors `createPkcePair` in @dayvise/auth, which needs no client secret. */
async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const verifier = base64Url(bytes);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

/* -------------------------------- providers ------------------------------- */

const PROVIDER_LABELS: Record<string, { label: string; hint?: string }> = {
  google: { label: "Google" },
  github: { label: "GitHub" },
  workos: { label: "Personal account", hint: "Sign in with Google, GitHub or email." },
  "workos-work": { label: "Organization account", hint: "Sign in to an organization you belong to." },
  local: { label: "Local development", hint: "Sign in as this machine's local identity." },
};

interface ProviderStatus {
  readonly name: string;
  readonly configured: boolean;
}

/* ---------------------------------- page ---------------------------------- */

export function LoginPage() {
  const { signIn, signOut } = useSession();
  const tokenStore = useTokenStore();
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderStatus[] | null>(null);
  const [providerLoadFailed, setProviderLoadFailed] = useState(false);
  const [mailingListUrl, setMailingListUrl] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const processedCodes = useRef(new Set<string>());

  // PKCE state lives in this tab's origin-scoped session storage. Returning to
  // a configured alias (for example localhost from 127.0.0.1) loses that state.
  const redirectUri = `${window.location.origin}/auth/callback`;

  // Only offer what this deployment can actually complete, and say so when it can
  // offer nothing — a sign-in page full of buttons that all fail is worse than an
  // empty one that explains why.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Through the SAME base every other call uses. These three used `endpoints().api`, an
        // absolute origin, while the rest of the app calls a relative /v1 that the dev server
        // proxies -- so the auth calls were cross-origin and failed CORS wherever the app and the
        // API are not one origin, which includes local development. Sign-in could not complete,
        // and the failure looked like a network fault rather than a base-URL mistake.
        const response = await fetch(`${resolveApiBaseUrl()}/auth/providers`);
        if (!response.ok) throw new Error(`the API answered ${response.status}`);
        const body = (await response.json()) as { providers: ProviderStatus[]; alpha?: { mailingListUrl: string } };
        if (!cancelled) {
          setProviders(body.providers);
          setMailingListUrl(body.alpha?.mailingListUrl ?? null);
        }
      } catch {
        if (!cancelled) {
          setProviders([]);
          setProviderLoadFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const completeSignIn = useCallback(
    async (code: string, callbackState: string | null) => {
      setCompleting(true);
      setError(null);
      try {
        const verifier = requireSignInVerifier(sessionStorage, callbackState);
        const response = await fetch(`${resolveApiBaseUrl()}/auth/token`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ code, code_verifier: verifier, redirect_uri: redirectUri }),
        });
        const body = (await response.json()) as
          | { sessionToken: string; identityId?: string; user: { userId: string; displayName: string | null; primaryEmail: string | null }; plane: "personal" | "work"; workspaces: WorkspaceSummary[] }
          | { error: string; message: string };
        if (!response.ok || "error" in body) {
          throw new Error("message" in body ? body.message : "sign-in failed");
        }

        // One store: the provider was constructed with browserTokenStore, so
        // this write is what every later request — and reload hydration — reads.
        await tokenStore.write(body.sessionToken);
        clearPendingSignIn(sessionStorage);
        await signIn({
          token: body.sessionToken,
          account: {
            identityId: body.identityId ?? body.user.userId,
            label: body.user.displayName ?? body.user.primaryEmail ?? "You",
            plane: body.plane,
            email: body.user.primaryEmail,
            userId: body.user.userId,
          },
          workspaces: body.workspaces.map(toSessionWorkspace),
        });
        window.history.replaceState(null, "", window.location.pathname);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "sign-in failed");
      } finally {
        setCompleting(false);
      }
    },
    [redirectUri, signIn, tokenStore],
  );

  // The provider sends the browser back here with a single-use code.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const providerError = params.get("error");
    if (providerError !== null) {
      setError(`The provider refused the sign-in: ${providerError}`);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    if (code === null || processedCodes.current.has(code)) return;
    processedCodes.current.add(code);
    void completeSignIn(code, params.get("state"));
  }, [completeSignIn]);

  const startSignIn = async (provider: string): Promise<void> => {
    setError(null);
    try {
      const pair = await createPkcePair();
      clearPendingSignIn(sessionStorage);

      const url = new URL(`${resolveApiBaseUrl()}/auth/${provider}/start`, window.location.origin);
      url.searchParams.set("redirect_uri", redirectUri);
      url.searchParams.set("code_challenge", pair.challenge);

      const response = await fetch(url.toString());
      const body = (await response.json()) as { authorizeUrl?: string; callbackState?: string; message?: string };
      if (!response.ok || !body.authorizeUrl || !body.callbackState) {
        throw new Error(body.message ?? `the API answered ${response.status}`);
      }
      savePendingSignIn(sessionStorage, pair.verifier, body.callbackState);
      // The API decides where the provider is; the browser never builds that URL.
      window.location.assign(body.authorizeUrl);
    } catch (caught) {
      clearPendingSignIn(sessionStorage);
      setError(caught instanceof Error ? caught.message : "sign-in could not be started");
    }
  };

  const configured = providers?.filter((provider) => provider.configured) ?? [];
  const ready = providers !== null;

  return (
    <div style={{ ...stackStyle("var(--dayvise-space-md)"), maxWidth: "420px", margin: "10vh auto", padding: "var(--dayvise-space-md)" }}>
      <h1 style={{ margin: 0, fontSize: "var(--dayvise-font-size-xl)" }}><DayviseBrand /></h1>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>Sign in to run agents that keep working while your laptop is closed.</p>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>Dayvise is in a limited alpha. <a href={`${endpoints().marketing}/waitlist`}>Join the mailing list</a> for launch updates.</p>

      <Card title="Continue with">
        {mailingListUrl ? <p>Dayvise is in a limited alpha. <a href={mailingListUrl}>Join the mailing list for access updates</a>.</p> : null}
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          {!ready ? (
            <span style={MUTED_TEXT}>Checking which sign-in methods this deployment supports…</span>
          ) : providerLoadFailed ? (
            <span role="alert" style={MUTED_TEXT}>Dayvise could not reach the sign-in service. Check your connection and reload to try again.</span>
          ) : configured.length === 0 ? (
            <span style={MUTED_TEXT}>
              This deployment has no sign-in provider configured yet. An operator needs to set the provider credentials before anyone can sign in.
            </span>
          ) : (
            configured.map((provider) => (
              <Button
                key={provider.name}
                variant={provider.name === "google" || provider.name === "workos" ? "primary" : "secondary"}
                fullWidth
                loading={completing}
                onClick={() => void startSignIn(provider.name)}
              >
                {PROVIDER_LABELS[provider.name]?.label ?? provider.name}
              </Button>
            ))
          )}
          {error ? <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>{error}</span> : null}
        </div>
      </Card>

      <Button variant="ghost" onClick={() => void signOut()}>
        Sign out of this browser
      </Button>
    </div>
  );
}

interface WorkspaceSummary {
  readonly workspaceId: string;
  readonly name: string;
  readonly kind: "personal" | "team";
  readonly plan: string;
  readonly planStatus?: SessionWorkspace["planStatus"];
  readonly role?: SessionWorkspace["role"];
}

function toSessionWorkspace(workspace: WorkspaceSummary): SessionWorkspace {
  return {
    workspaceId: workspace.workspaceId as SessionWorkspace["workspaceId"],
    name: workspace.name,
    kind: workspace.kind,
    plan: workspace.plan,
    planStatus: workspace.planStatus,
    role: workspace.role ?? "owner",
  };
}
